/**
 * EOS Accountability Chart - Horizontal Slider by Seat (Slack-style)
 * 
 * Features:
 * - Horizontal scrollable columns by SEAT (not person)
 * - Responsibility cards within each seat (EOS "5 Roles" concept)
 * - Modal-based add/edit (no side panels)
 * - Inline "+ Add Seat" and "+ Add Responsibility"
 * - Max 5 responsibilities per seat (warn, not block)
 * - Drag-and-drop reordering within seat columns
 */

import React, { useState, useEffect, useRef } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Users, 
  Plus, 
  ChevronLeft, 
  ChevronRight,
  Trash2,
  Edit2,
  GripVertical,
  Briefcase,
  Target,
  AlertTriangle,
  CheckCircle2,
  HelpCircle,
  Crown,
  Settings,
  DollarSign,
  HeadphonesIcon,
  Building
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Seat category configuration
const CATEGORY_CONFIG = {
  leadership: { 
    label: 'Leadership', 
    color: 'bg-purple-500', 
    icon: Crown, 
    textColor: 'text-purple-500',
    borderColor: 'border-purple-400'
  },
  sales: { 
    label: 'Sales', 
    color: 'bg-green-500', 
    icon: DollarSign, 
    textColor: 'text-green-500',
    borderColor: 'border-green-400'
  },
  operations: { 
    label: 'Operations', 
    color: 'bg-blue-500', 
    icon: Settings, 
    textColor: 'text-blue-500',
    borderColor: 'border-blue-400'
  },
  marketing: { 
    label: 'Marketing', 
    color: 'bg-pink-500', 
    icon: Target, 
    textColor: 'text-pink-500',
    borderColor: 'border-pink-400'
  },
  support: { 
    label: 'Support', 
    color: 'bg-orange-500', 
    icon: HeadphonesIcon, 
    textColor: 'text-orange-500',
    borderColor: 'border-orange-400'
  },
  admin: { 
    label: 'Admin', 
    color: 'bg-slate-500', 
    icon: Building, 
    textColor: 'text-slate-400',
    borderColor: 'border-slate-400'
  },
  custom: { 
    label: 'Custom', 
    color: 'bg-cyan-500', 
    icon: Briefcase, 
    textColor: 'text-cyan-500',
    borderColor: 'border-cyan-400'
  },
};

// Responsibility status configuration
const RESPONSIBILITY_STATUS = {
  defined: { label: 'Defined', color: 'bg-green-500', icon: CheckCircle2 },
  needs_review: { label: 'Needs Review', color: 'bg-yellow-500', icon: AlertTriangle },
  missing: { label: 'Missing', color: 'bg-red-500', icon: HelpCircle },
};

// Responsibility Card Content Component
const ResponsibilityCardContent = ({ responsibility, onClick, isDragging }) => {
  const status = RESPONSIBILITY_STATUS[responsibility.status] || RESPONSIBILITY_STATUS.defined;
  const StatusIcon = status.icon;
  
  return (
    <Card 
      className={`dark:bg-slate-700/50 border-slate-600 hover:border-blue-400 cursor-pointer transition-all hover:shadow-lg ${isDragging ? 'shadow-xl ring-2 ring-blue-400/50' : ''}`}
      onClick={() => !isDragging && onClick(responsibility)}
      data-testid={`responsibility-card-${responsibility.id}`}
    >
      <CardContent className="p-3">
        {/* Status Badge */}
        <div className="flex items-center justify-between mb-2">
          <Badge 
            variant="outline" 
            className={`${status.color} text-white border-0 text-xs`}
          >
            <StatusIcon className="h-3 w-3 mr-1" />
            {status.label}
          </Badge>
        </div>
        
        {/* Title */}
        <p className="text-sm font-medium dark:text-white line-clamp-2">
          {responsibility.title}
        </p>
        
        {/* Description preview */}
        {responsibility.description && (
          <p className="text-xs text-slate-400 mt-1 line-clamp-1">
            {responsibility.description}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

// Sortable Responsibility Card Component
const SortableResponsibilityCard = ({ responsibility, onClick }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: responsibility.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative group/card">
      <button
        {...attributes}
        {...listeners}
        className="absolute left-1 top-1/2 -translate-y-1/2 z-10 cursor-grab active:cursor-grabbing text-slate-500 hover:text-slate-300 opacity-0 group-hover/card:opacity-100 transition-opacity bg-slate-800/80 rounded p-1"
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <ResponsibilityCardContent responsibility={responsibility} onClick={onClick} isDragging={isDragging} />
    </div>
  );
};

// Seat Column Component
const SeatColumn = ({ seat, onEditSeat, onDeleteSeat, onAddResponsibility, onResponsibilityClick, onReorderResponsibilities }) => {
  const category = CATEGORY_CONFIG[seat.department?.toLowerCase()] || CATEGORY_CONFIG.custom;
  const CategoryIcon = category.icon;
  const responsibilities = seat.responsibilities || [];
  const responsibilityIds = responsibilities.map(r => r.id);
  
  const roleCount = responsibilities.length;
  const isOverLimit = roleCount > 5;
  
  return (
    <div className="flex-shrink-0 w-72 bg-slate-800/40 rounded-lg border border-slate-700" data-testid={`seat-column-${seat.id}`}>
      {/* Column Header */}
      <div className={`p-3 border-b border-slate-700 sticky top-0 bg-slate-800/90 backdrop-blur-sm rounded-t-lg border-l-4 ${category.borderColor}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className={`p-1.5 rounded ${category.color}`}>
              <CategoryIcon className="h-4 w-4 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-sm dark:text-white truncate">
                {seat.title}
              </p>
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <Badge variant="outline" className="text-[10px] py-0">{category.label}</Badge>
                <span className={isOverLimit ? 'text-yellow-400' : ''}>
                  {roleCount}/5 roles
                </span>
              </div>
            </div>
          </div>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => onEditSeat(seat)}
            >
              <Edit2 className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-destructive"
              onClick={() => onDeleteSeat(seat)}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        </div>
        
        {/* Person Assignment (Read-only) */}
        {seat.person_name && (
          <div className="mt-2 p-1.5 bg-slate-700/50 rounded text-xs flex items-center gap-1">
            <Users className="h-3 w-3 text-blue-400" />
            <span className="text-slate-300">{seat.person_name}</span>
          </div>
        )}
      </div>
      
      {/* Responsibilities List */}
      <div className="p-2 space-y-2 max-h-[calc(100vh-350px)] overflow-y-auto scrollbar-thin">
        {isOverLimit && (
          <div className="p-2 bg-yellow-900/30 border border-yellow-700 rounded-lg text-xs text-yellow-400 flex items-center gap-2">
            <AlertTriangle className="h-3 w-3" />
            EOS recommends max 5 roles per seat
          </div>
        )}
        
        <SortableContext items={responsibilityIds} strategy={verticalListSortingStrategy}>
          {responsibilities.map(resp => (
            <SortableResponsibilityCard 
              key={resp.id} 
              responsibility={resp} 
              onClick={() => onResponsibilityClick(seat, resp)}
            />
          ))}
        </SortableContext>
        
        {responsibilities.length === 0 && (
          <p className="text-center text-slate-500 text-sm py-4">No roles defined</p>
        )}
        
        {/* Add Responsibility Button */}
        <Button
          variant="ghost"
          className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-700/50 border border-dashed border-slate-600"
          onClick={() => onAddResponsibility(seat)}
          data-testid={`add-responsibility-btn-${seat.id}`}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Role
        </Button>
      </div>
    </div>
  );
};

const EOSAccountabilityChartSlider = () => {
  const [chart, setChart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const scrollContainerRef = useRef(null);
  const [activeId, setActiveId] = useState(null);
  
  // Drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  
  // Modal States
  const [seatDialogOpen, setSeatDialogOpen] = useState(false);
  const [responsibilityDialogOpen, setResponsibilityDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState(null);
  const [editingSeat, setEditingSeat] = useState(null);
  const [editingResponsibility, setEditingResponsibility] = useState(null);
  const [currentSeat, setCurrentSeat] = useState(null);
  
  // Seat Form State
  const [seatForm, setSeatForm] = useState({
    title: '',
    department: 'custom',
    description: '',
    level: 0
  });
  
  // Responsibility Form State
  const [responsibilityForm, setResponsibilityForm] = useState({
    title: '',
    description: '',
    status: 'defined'
  });

  useEffect(() => {
    fetchChart();
  }, []);

  const fetchChart = async () => {
    try {
      const response = await axios.get(`${API}/eos/accountability-chart`);
      // Transform seats to include responsibilities array
      const chartData = response.data;
      if (chartData?.seats) {
        chartData.seats = chartData.seats.map(seat => ({
          ...seat,
          responsibilities: (seat.five_roles || []).map((role, idx) => ({
            id: `${seat.id}_role_${idx}`,
            title: role,
            description: '',
            status: role ? 'defined' : 'missing'
          }))
        }));
      }
      setChart(chartData);
    } catch (error) {
      console.error('Error fetching accountability chart:', error);
      toast.error('Failed to load accountability chart');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSeat = () => {
    setEditingSeat(null);
    setSeatForm({
      title: '',
      department: 'custom',
      description: '',
      level: 0
    });
    setSeatDialogOpen(true);
  };

  const handleEditSeat = (seat) => {
    setEditingSeat(seat);
    setSeatForm({
      title: seat.title,
      department: seat.department || 'custom',
      description: seat.description || '',
      level: seat.level || 0
    });
    setSeatDialogOpen(true);
  };

  const handleSaveSeat = async () => {
    try {
      if (!seatForm.title.trim()) {
        toast.error('Please enter a seat title');
        return;
      }

      const newSeat = {
        id: editingSeat?.id || `seat_${Date.now()}`,
        title: seatForm.title,
        department: seatForm.department,
        description: seatForm.description,
        level: parseInt(seatForm.level) || 0,
        five_roles: editingSeat?.five_roles || [],
        person_user_id: editingSeat?.person_user_id || null,
        person_name: editingSeat?.person_name || null,
        key_metrics: editingSeat?.key_metrics || [],
        gwc_score: editingSeat?.gwc_score || null
      };

      let updatedSeats;
      if (editingSeat) {
        updatedSeats = chart.seats.map(s => s.id === editingSeat.id ? { ...s, ...newSeat } : s);
      } else {
        updatedSeats = [...(chart?.seats || []), newSeat];
      }

      // Save to backend (strip responsibilities for storage)
      const seatsForBackend = updatedSeats.map(s => {
        const { responsibilities, ...rest } = s;
        return rest;
      });
      
      await axios.post(`${API}/eos/accountability-chart`, { seats: seatsForBackend });
      
      toast.success(editingSeat ? 'Seat updated' : 'Seat added');
      setSeatDialogOpen(false);
      fetchChart();
    } catch (error) {
      console.error('Error saving seat:', error);
      toast.error('Failed to save seat');
    }
  };

  const handleDeleteSeat = (seat) => {
    setDeletingItem({ type: 'seat', item: seat });
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!deletingItem) return;
    
    try {
      if (deletingItem.type === 'seat') {
        const updatedSeats = chart.seats.filter(s => s.id !== deletingItem.item.id);
        const seatsForBackend = updatedSeats.map(s => {
          const { responsibilities, ...rest } = s;
          return rest;
        });
        await axios.post(`${API}/eos/accountability-chart`, { seats: seatsForBackend });
        toast.success('Seat deleted');
      } else if (deletingItem.type === 'responsibility') {
        const { seat, responsibility } = deletingItem.item;
        const updatedRoles = seat.five_roles.filter((_, idx) => `${seat.id}_role_${idx}` !== responsibility.id);
        const updatedSeat = { ...seat, five_roles: updatedRoles };
        const updatedSeats = chart.seats.map(s => s.id === seat.id ? updatedSeat : s);
        const seatsForBackend = updatedSeats.map(s => {
          const { responsibilities, ...rest } = s;
          return rest;
        });
        await axios.post(`${API}/eos/accountability-chart`, { seats: seatsForBackend });
        toast.success('Role deleted');
      }
      
      setDeleteDialogOpen(false);
      setDeletingItem(null);
      fetchChart();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Failed to delete');
    }
  };

  const handleAddResponsibility = (seat) => {
    if (seat.responsibilities?.length >= 5) {
      toast.warning('EOS recommends max 5 roles per seat. You can still add more if needed.');
    }
    setCurrentSeat(seat);
    setEditingResponsibility(null);
    setResponsibilityForm({
      title: '',
      description: '',
      status: 'defined'
    });
    setResponsibilityDialogOpen(true);
  };

  const handleResponsibilityClick = (seat, responsibility) => {
    setCurrentSeat(seat);
    setEditingResponsibility(responsibility);
    setResponsibilityForm({
      title: responsibility.title,
      description: responsibility.description || '',
      status: responsibility.status || 'defined'
    });
    setResponsibilityDialogOpen(true);
  };

  const handleSaveResponsibility = async () => {
    try {
      if (!responsibilityForm.title.trim()) {
        toast.error('Please enter a role title');
        return;
      }
      if (!currentSeat) return;

      let updatedRoles;
      if (editingResponsibility) {
        // Update existing role
        const roleIndex = currentSeat.five_roles.findIndex((_, idx) => 
          `${currentSeat.id}_role_${idx}` === editingResponsibility.id
        );
        if (roleIndex !== -1) {
          updatedRoles = [...currentSeat.five_roles];
          updatedRoles[roleIndex] = responsibilityForm.title;
        } else {
          updatedRoles = currentSeat.five_roles;
        }
      } else {
        // Add new role
        updatedRoles = [...(currentSeat.five_roles || []), responsibilityForm.title];
      }

      const updatedSeat = { ...currentSeat, five_roles: updatedRoles };
      const updatedSeats = chart.seats.map(s => s.id === currentSeat.id ? updatedSeat : s);
      
      const seatsForBackend = updatedSeats.map(s => {
        const { responsibilities, ...rest } = s;
        return rest;
      });
      
      await axios.post(`${API}/eos/accountability-chart`, { seats: seatsForBackend });
      
      toast.success(editingResponsibility ? 'Role updated' : 'Role added');
      setResponsibilityDialogOpen(false);
      fetchChart();
    } catch (error) {
      console.error('Error saving responsibility:', error);
      toast.error('Failed to save role');
    }
  };

  const handleDeleteResponsibility = () => {
    if (!currentSeat || !editingResponsibility) return;
    setDeletingItem({ type: 'responsibility', item: { seat: currentSeat, responsibility: editingResponsibility } });
    setResponsibilityDialogOpen(false);
    setDeleteDialogOpen(true);
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  // Drag and drop handlers
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    
    if (!over || active.id === over.id) return;

    // Find which seat contains the responsibilities being reordered
    let sourceSeat = null;
    let activeResp = null;
    let overResp = null;

    for (const seat of (chart?.seats || [])) {
      const resps = seat.responsibilities || [];
      const foundActive = resps.find(r => r.id === active.id);
      const foundOver = resps.find(r => r.id === over.id);
      
      if (foundActive) {
        sourceSeat = seat;
        activeResp = foundActive;
      }
      if (foundOver) {
        overResp = foundOver;
      }
    }

    if (!sourceSeat || !activeResp || !overResp) return;
    
    // Only allow reordering within the same seat
    const activeInSeat = sourceSeat.responsibilities?.find(r => r.id === active.id);
    const overInSeat = sourceSeat.responsibilities?.find(r => r.id === over.id);
    
    if (!activeInSeat || !overInSeat) {
      toast.info('Roles can only be reordered within the same seat');
      return;
    }

    const oldIndex = sourceSeat.five_roles.findIndex((_, idx) => `${sourceSeat.id}_role_${idx}` === active.id);
    const newIndex = sourceSeat.five_roles.findIndex((_, idx) => `${sourceSeat.id}_role_${idx}` === over.id);
    
    if (oldIndex === -1 || newIndex === -1) return;
    
    const reorderedRoles = arrayMove(sourceSeat.five_roles, oldIndex, newIndex);
    const updatedSeat = { ...sourceSeat, five_roles: reorderedRoles };
    const updatedSeats = chart.seats.map(s => s.id === sourceSeat.id ? updatedSeat : s);
    
    try {
      const seatsForBackend = updatedSeats.map(s => {
        const { responsibilities, ...rest } = s;
        return rest;
      });
      await axios.post(`${API}/eos/accountability-chart`, { seats: seatsForBackend });
      toast.success('Role order updated');
      fetchChart();
    } catch (error) {
      console.error('Error reordering:', error);
      toast.error('Failed to reorder');
    }
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  // Get active responsibility for drag overlay
  let activeResponsibility = null;
  if (activeId && chart?.seats) {
    for (const seat of chart.seats) {
      const found = seat.responsibilities?.find(r => r.id === activeId);
      if (found) {
        activeResponsibility = found;
        break;
      }
    }
  }

  // Filter seats based on category
  const seats = chart?.seats || [];
  const filteredSeats = categoryFilter === 'all' 
    ? seats 
    : seats.filter(s => (s.department?.toLowerCase() || 'custom') === categoryFilter);

  // Calculate stats
  const stats = {
    total: seats.length,
    leadership: seats.filter(s => s.department?.toLowerCase() === 'leadership').length,
    sales: seats.filter(s => s.department?.toLowerCase() === 'sales').length,
    operations: seats.filter(s => s.department?.toLowerCase() === 'operations').length,
    other: seats.filter(s => !['leadership', 'sales', 'operations'].includes(s.department?.toLowerCase())).length,
    totalRoles: seats.reduce((sum, s) => sum + (s.five_roles?.length || 0), 0)
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading Accountability Chart...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-4" data-testid="accountability-chart-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className="h-8 w-8 text-purple-500" />
            <div>
              <h1 className="text-2xl font-bold dark:text-white">Accountability Chart</h1>
              <p className="text-sm text-muted-foreground">
                Define seats, not people. Structure your organization by role.
              </p>
            </div>
          </div>
          
          <Button onClick={handleAddSeat} data-testid="add-seat-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Seat
          </Button>
        </div>

        {/* Category Filter Tabs */}
        <Tabs value={categoryFilter} onValueChange={setCategoryFilter} className="w-full">
          <TabsList className="bg-slate-800/40">
            <TabsTrigger value="all" data-testid="tab-all">All ({stats.total})</TabsTrigger>
            <TabsTrigger value="leadership" data-testid="tab-leadership">
              <Crown className="h-3 w-3 mr-1 text-purple-500" />
              Leadership ({stats.leadership})
            </TabsTrigger>
            <TabsTrigger value="sales" data-testid="tab-sales">
              <DollarSign className="h-3 w-3 mr-1 text-green-500" />
              Sales ({stats.sales})
            </TabsTrigger>
            <TabsTrigger value="operations" data-testid="tab-operations">
              <Settings className="h-3 w-3 mr-1 text-blue-500" />
              Operations ({stats.operations})
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Stats Bar */}
        <div className="flex gap-4 p-3 bg-slate-800/40 rounded-lg border border-slate-700">
          <div className="flex items-center gap-2">
            <span className="text-slate-400 text-sm">Total Seats:</span>
            <Badge variant="outline" className="dark:bg-slate-700">{filteredSeats.length}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-slate-400 text-sm">Total Roles:</span>
            <Badge variant="outline" className="dark:bg-slate-700">{stats.totalRoles}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-slate-400 text-sm">Avg Roles/Seat:</span>
            <Badge variant="outline" className="dark:bg-slate-700">
              {stats.total > 0 ? (stats.totalRoles / stats.total).toFixed(1) : 0}
            </Badge>
          </div>
        </div>

        {/* Info Card */}
        <div className="p-3 bg-blue-950/30 border border-blue-800 rounded-lg text-sm">
          <p className="text-blue-300">
            💡 <strong>EOS Tip:</strong> Each seat should have exactly 5 key roles that define that position. 
            Focus on the seat, not the person who fills it.
          </p>
        </div>

        {/* Horizontal Slider Container with Drag and Drop */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <div className="relative">
            {/* Scroll Buttons */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-slate-800/80 hover:bg-slate-700 rounded-full shadow-lg"
              onClick={scrollLeft}
              data-testid="scroll-left-btn"
            >
              <ChevronLeft className="h-6 w-6" />
            </Button>
            
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-slate-800/80 hover:bg-slate-700 rounded-full shadow-lg"
              onClick={scrollRight}
              data-testid="scroll-right-btn"
            >
              <ChevronRight className="h-6 w-6" />
            </Button>
            
            {/* Scrollable Container */}
            <div 
              ref={scrollContainerRef}
              className="flex gap-4 overflow-x-auto px-10 pb-4 scrollbar-thin"
              style={{ scrollbarWidth: 'thin' }}
              data-testid="accountability-slider"
            >
              {filteredSeats.map(seat => (
                <SeatColumn
                  key={seat.id}
                  seat={seat}
                  onEditSeat={handleEditSeat}
                  onDeleteSeat={handleDeleteSeat}
                  onAddResponsibility={handleAddResponsibility}
                  onResponsibilityClick={handleResponsibilityClick}
                />
              ))}
              
              {/* Add Seat Column */}
              <div 
                className="flex-shrink-0 w-72 bg-slate-800/20 rounded-lg border-2 border-dashed border-slate-700 flex items-center justify-center cursor-pointer hover:bg-slate-800/40 hover:border-slate-600 transition-colors min-h-[200px]"
                onClick={handleAddSeat}
                data-testid="add-seat-column"
              >
                <div className="text-center p-6">
                  <Plus className="h-10 w-10 mx-auto mb-3 text-slate-500" />
                  <p className="text-slate-400 font-medium">Add New Seat</p>
                  <p className="text-xs text-slate-500 mt-1">Define a new role in your org</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Drag Overlay */}
          <DragOverlay>
            {activeResponsibility && (
              <div className="w-64 opacity-90">
                <ResponsibilityCardContent 
                  responsibility={activeResponsibility} 
                  onClick={() => {}} 
                  isDragging={true}
                />
              </div>
            )}
          </DragOverlay>
        </DndContext>

        {/* Empty State */}
        {filteredSeats.length === 0 && categoryFilter !== 'all' && (
          <div className="text-center py-8 text-slate-500">
            <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No {CATEGORY_CONFIG[categoryFilter]?.label} seats defined.</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={handleAddSeat}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add a {CATEGORY_CONFIG[categoryFilter]?.label} seat
            </Button>
          </div>
        )}
      </div>

      {/* Seat Dialog */}
      <Dialog open={seatDialogOpen} onOpenChange={setSeatDialogOpen}>
        <DialogContent className="dark:bg-slate-800 dark:border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="dark:text-white flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-purple-500" />
              {editingSeat ? 'Edit Seat' : 'Add New Seat'}
            </DialogTitle>
            <DialogDescription className="dark:text-slate-400">
              Define a seat (role) in your organization structure
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="dark:text-slate-200">Seat Title *</Label>
              <Input
                value={seatForm.title}
                onChange={(e) => setSeatForm({ ...seatForm, title: e.target.value })}
                placeholder="e.g., Visionary, Integrator, Sales Manager"
                className="dark:bg-slate-700 dark:border-slate-600"
                data-testid="seat-title-input"
              />
            </div>
            
            <div>
              <Label className="dark:text-slate-200">Category</Label>
              <Select 
                value={seatForm.department} 
                onValueChange={(val) => setSeatForm({ ...seatForm, department: val })}
              >
                <SelectTrigger className="dark:bg-slate-700 dark:border-slate-600" data-testid="seat-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="dark:bg-slate-800 dark:border-slate-600">
                  <SelectItem value="leadership">
                    <div className="flex items-center gap-2">
                      <Crown className="h-4 w-4 text-purple-500" />
                      Leadership
                    </div>
                  </SelectItem>
                  <SelectItem value="sales">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-green-500" />
                      Sales
                    </div>
                  </SelectItem>
                  <SelectItem value="operations">
                    <div className="flex items-center gap-2">
                      <Settings className="h-4 w-4 text-blue-500" />
                      Operations
                    </div>
                  </SelectItem>
                  <SelectItem value="marketing">
                    <div className="flex items-center gap-2">
                      <Target className="h-4 w-4 text-pink-500" />
                      Marketing
                    </div>
                  </SelectItem>
                  <SelectItem value="support">
                    <div className="flex items-center gap-2">
                      <HeadphonesIcon className="h-4 w-4 text-orange-500" />
                      Support
                    </div>
                  </SelectItem>
                  <SelectItem value="admin">
                    <div className="flex items-center gap-2">
                      <Building className="h-4 w-4 text-slate-500" />
                      Admin
                    </div>
                  </SelectItem>
                  <SelectItem value="custom">
                    <div className="flex items-center gap-2">
                      <Briefcase className="h-4 w-4 text-cyan-500" />
                      Custom
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="dark:text-slate-200">Description (Optional)</Label>
              <Textarea
                value={seatForm.description}
                onChange={(e) => setSeatForm({ ...seatForm, description: e.target.value })}
                placeholder="Brief description of this seat's purpose..."
                className="dark:bg-slate-700 dark:border-slate-600"
                rows={2}
              />
            </div>
            
            <div>
              <Label className="dark:text-slate-200">Organization Level (0 = Leadership)</Label>
              <Input
                type="number"
                min="0"
                value={seatForm.level}
                onChange={(e) => setSeatForm({ ...seatForm, level: e.target.value })}
                className="dark:bg-slate-700 dark:border-slate-600"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSeatDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveSeat} data-testid="save-seat-btn">
              {editingSeat ? 'Update Seat' : 'Add Seat'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Responsibility Dialog */}
      <Dialog open={responsibilityDialogOpen} onOpenChange={setResponsibilityDialogOpen}>
        <DialogContent className="dark:bg-slate-800 dark:border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="dark:text-white flex items-center gap-2">
              <Target className="h-5 w-5 text-blue-500" />
              {editingResponsibility ? 'Edit Role' : 'Add New Role'}
            </DialogTitle>
            <DialogDescription className="dark:text-slate-400">
              {currentSeat && (
                <span>Adding role to: <strong>{currentSeat.title}</strong></span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="dark:text-slate-200">Role Title *</Label>
              <Input
                value={responsibilityForm.title}
                onChange={(e) => setResponsibilityForm({ ...responsibilityForm, title: e.target.value })}
                placeholder="e.g., Lead generation, Customer retention"
                className="dark:bg-slate-700 dark:border-slate-600"
                data-testid="responsibility-title-input"
              />
            </div>
            
            <div>
              <Label className="dark:text-slate-200">Description (Optional)</Label>
              <Textarea
                value={responsibilityForm.description}
                onChange={(e) => setResponsibilityForm({ ...responsibilityForm, description: e.target.value })}
                placeholder="More details about this responsibility..."
                className="dark:bg-slate-700 dark:border-slate-600"
                rows={2}
              />
            </div>
            
            <div>
              <Label className="dark:text-slate-200">Status</Label>
              <Select 
                value={responsibilityForm.status} 
                onValueChange={(val) => setResponsibilityForm({ ...responsibilityForm, status: val })}
              >
                <SelectTrigger className="dark:bg-slate-700 dark:border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="dark:bg-slate-800 dark:border-slate-600">
                  <SelectItem value="defined">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      Defined
                    </div>
                  </SelectItem>
                  <SelectItem value="needs_review">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-yellow-500" />
                      Needs Review
                    </div>
                  </SelectItem>
                  <SelectItem value="missing">
                    <div className="flex items-center gap-2">
                      <HelpCircle className="h-4 w-4 text-red-500" />
                      Missing/Undefined
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {currentSeat?.responsibilities?.length >= 5 && !editingResponsibility && (
              <div className="p-3 bg-yellow-900/30 border border-yellow-700 rounded-lg text-sm text-yellow-400">
                <AlertTriangle className="h-4 w-4 inline mr-2" />
                This seat already has 5+ roles. EOS recommends focusing on exactly 5 key roles per seat.
              </div>
            )}
          </div>
          
          <DialogFooter className="flex justify-between">
            {editingResponsibility && (
              <Button variant="destructive" onClick={handleDeleteResponsibility}>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setResponsibilityDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveResponsibility} data-testid="save-responsibility-btn">
                {editingResponsibility ? 'Update Role' : 'Add Role'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="dark:bg-slate-800 dark:border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="dark:text-white">
              Delete {deletingItem?.type === 'seat' ? 'Seat' : 'Role'}?
            </AlertDialogTitle>
            <AlertDialogDescription className="dark:text-slate-400">
              This action cannot be undone. 
              {deletingItem?.type === 'seat' 
                ? ' All roles in this seat will also be deleted.'
                : ' This role will be removed from the seat.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="dark:bg-slate-700 dark:border-slate-600">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              className="bg-red-600 hover:bg-red-700"
              onClick={confirmDelete}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};

export default EOSAccountabilityChartSlider;
